import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Star,
  User,
  UserPlus,
  CheckCircle,
  XCircle,
  Megaphone,
  Bell,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { spring, duration, easing } from "@/lib/motion";
import type { NotificationResponse } from "@/types/user";
import {
  getNotificationTitle,
  getNotificationBody,
  getRelativeTime,
} from "@/types/user";

// ─── Types ───────────────────────────────────────────────────────────────────

interface NotificationItemProps {
  /** The notification data to render. */
  notification: NotificationResponse;
  /** Called when the user clicks the notification (marks as read + navigates). */
  onRead?: (notificationId: string) => Promise<void>;
  /** Whether to show the full body text or truncate to 2 lines. */
  variant?: "compact" | "full";
  /** Index for staggered entrance animation. */
  index?: number;
  /** Additional CSS classes for the container. */
  className?: string;
}

// ─── Icon Mapping ────────────────────────────────────────────────────────────

/**
 * Map notification type strings to Lucide icon components and their colors.
 */
function getNotificationIcon(notificationType: string) {
  switch (notificationType) {
    case "new_thesis":
      return { Icon: BookOpen, colorClass: "text-blue-500 bg-blue-500/10" };
    case "new_review":
      return { Icon: Star, colorClass: "text-amber-500 bg-amber-500/10" };
    case "author_cv_updated":
      return { Icon: User, colorClass: "text-green-500 bg-green-500/10" };
    case "author_claim_request":
      return { Icon: UserPlus, colorClass: "text-purple-500 bg-purple-500/10" };
    case "claim_approved":
      return {
        Icon: CheckCircle,
        colorClass: "text-green-500 bg-green-500/10",
      };
    case "claim_rejected":
      return { Icon: XCircle, colorClass: "text-red-500 bg-red-500/10" };
    case "system_announcement":
      return {
        Icon: Megaphone,
        colorClass: "text-purple-500 bg-purple-500/10",
      };
    default:
      return { Icon: Bell, colorClass: "text-muted-foreground bg-muted" };
  }
}

// ─── Animation Variants ──────────────────────────────────────────────────────

/** Full-motion item variants with staggered entrance from right */
const itemVariants = {
  initial: { opacity: 0, x: 12 },
  animate: (index: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      type: "spring" as const,
      stiffness: spring.smooth.stiffness,
      damping: spring.smooth.damping + 5,
      delay: index * 0.03,
    },
  }),
  exit: {
    opacity: 0,
    x: -12,
    transition: {
      duration: duration.fast,
      ease: easing.easeIn,
    },
  },
};

/** Reduced-motion item variants — instant appearance, no slide */
const itemVariantsReduced = {
  initial: { opacity: 1, x: 0 },
  animate: () => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0 },
  }),
  exit: {
    opacity: 0,
    x: 0,
    transition: { duration: 0 },
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Individual notification item displayed in the notification panel or
 * notification center page.
 *
 * Features:
 * - Icon based on notification type (book for thesis, star for review, etc.)
 * - Title text in the current language
 * - Body text (truncated to 2 lines in compact mode, full in notification center)
 * - Relative timestamp ("3 min ago", "2 hours ago", "Yesterday")
 * - Unread indicator: blue dot on the left side
 * - Hover: subtle background color change
 * - Click: navigates to `notification.link`, marks as read
 * - Animated entrance: slide-in from right with stagger based on `index`
 *
 * Two variants:
 * - **compact**: Used in the header popover panel. Body truncated to 2 lines.
 * - **full**: Used in the notification center page. Full body displayed.
 */
export default function NotificationItem({
  notification,
  onRead,
  variant = "compact",
  index = 0,
  className,
}: NotificationItemProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const prefersReducedMotion = usePrefersReducedMotion();

  const { Icon, colorClass } = getNotificationIcon(
    notification.notification_type,
  );
  const title = getNotificationTitle(notification, lang);
  const body = getNotificationBody(notification, lang);
  const timeAgo = getRelativeTime(notification.created_at, lang);

  const isUnread = !notification.is_read;

  // Select variant set based on reduced-motion preference
  const activeVariants = prefersReducedMotion
    ? itemVariantsReduced
    : itemVariants;

  // ── Click handler ────────────────────────────────────────────────────────

  const handleClick = useCallback(async () => {
    // Mark as read
    if (isUnread && onRead) {
      try {
        await onRead(notification.id);
      } catch {
        // Best-effort — still navigate even if marking fails
      }
    }

    // Navigate to the notification's link
    if (notification.link) {
      // The link from the backend is a relative path like "/catalog/uuid"
      // We need to prepend the language prefix
      const targetPath = notification.link.startsWith("/")
        ? `/${lang}${notification.link}`
        : `/${lang}/${notification.link}`;

      navigate(targetPath);
    }
  }, [notification, isUnread, onRead, lang, navigate]);

  // ── Exact date tooltip text ──────────────────────────────────────────────

  const exactDate = new Date(notification.created_at).toLocaleString(
    lang === "fr" ? "fr-FR" : "en-US",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <motion.div
      variants={activeVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      custom={index}
      layout={!prefersReducedMotion}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`${title} — ${timeAgo}`}
      className={cn(
        "group relative flex cursor-pointer items-start gap-3 rounded-md px-3 py-3 transition-colors",
        "hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        isUnread ? "bg-accent/20 dark:bg-accent/10" : "bg-transparent",
        className,
      )}
    >
      {/* ── Unread indicator dot (animated shrink on mark-as-read) ──── */}
      <AnimatePresence>
        {isUnread && (
          <motion.div
            initial={prefersReducedMotion ? false : { scale: 0 }}
            animate={{ scale: 1 }}
            exit={
              prefersReducedMotion
                ? { opacity: 0, transition: { duration: 0 } }
                : {
                    scale: 0,
                    opacity: 0,
                    transition: {
                      duration: 0.2,
                      ease: easing.easeIn,
                    },
                  }
            }
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : {
                    type: "spring",
                    stiffness: spring.stiff.stiffness,
                    damping: spring.stiff.damping,
                  }
            }
            className="absolute left-0.5 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* ── Type icon ────────────────────────────────────────────────── */}
      <div
        className={cn(
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full",
          colorClass,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* ── Content ──────────────────────────────────────────────────── */}
      <div className="min-w-0 flex-1">
        {/* Title */}
        <p
          className={cn(
            "text-sm leading-snug",
            isUnread
              ? "font-semibold text-foreground"
              : "font-medium text-foreground/80",
          )}
        >
          {title}
        </p>

        {/* Body */}
        {body && (
          <p
            className={cn(
              "mt-0.5 text-xs leading-relaxed text-muted-foreground",
              variant === "compact" && "line-clamp-2",
            )}
            title={variant === "compact" ? body : undefined}
          >
            {body}
          </p>
        )}

        {/* Timestamp */}
        <p
          className="mt-1 text-[11px] text-muted-foreground/70"
          title={exactDate}
        >
          {timeAgo}
        </p>
      </div>

      {/* ── Link indicator ───────────────────────────────────────────── */}
      {notification.link && (
        <div className="flex flex-shrink-0 items-center self-center opacity-0 transition-opacity group-hover:opacity-60">
          <svg
            className="h-4 w-4 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      )}
    </motion.div>
  );
}
